import { feature, featureCollection, intersect, union, difference } from "@turf/turf";
import type { Feature, MultiPolygon, Polygon } from "geojson";
import { isFieldOutsideRotation } from "./jpbFieldClasses";

export type SimulatedMarkShareGeometries = {
   omdriftGeometry: Polygon | MultiPolygon | null;
   ikkeOmdriftGeometry: Polygon | MultiPolygon | null;
   section3Geometry: Polygon | MultiPolygon | null;
};

type PolygonalFeature = Feature<Polygon | MultiPolygon>;

function asPolygonalFeature(geometry: any): PolygonalFeature | null {
   if (!geometry) return null;
   if (geometry.type === "Feature") return asPolygonalFeature(geometry.geometry);
   if (geometry.type === "GeometryCollection" && Array.isArray(geometry.geometries)) {
      return unionFeatures(geometry.geometries.map((part: any) => asPolygonalFeature(part)).filter(Boolean) as PolygonalFeature[]);
   }
   if (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon") return null;
   return feature(geometry);
}

function intersectFeatures(a: PolygonalFeature, b: PolygonalFeature): PolygonalFeature | null {
   try {
      const result = intersect(featureCollection([a, b]));
      return result ? asPolygonalFeature(result.geometry) : null;
   } catch {
      return null;
   }
}

function unionFeatures(features: PolygonalFeature[]): PolygonalFeature | null {
   if (features.length === 0) return null;
   if (features.length === 1) return features[0];
   try {
      const result = union(featureCollection(features));
      return result ? asPolygonalFeature(result.geometry) : null;
   } catch {
      let accumulated = features[0];
      for (let index = 1; index < features.length; index++) {
         try {
            const next = union(featureCollection([accumulated, features[index]]));
            if (next) {
               const polygonal = asPolygonalFeature(next.geometry);
               if (polygonal) accumulated = polygonal;
            }
         } catch {
            // Skip a piece that cannot be dissolved and keep the rest.
         }
      }
      return accumulated;
   }
}

function differenceFeatures(base: PolygonalFeature, clip: PolygonalFeature | null): PolygonalFeature | null {
   if (!clip) return base;
   try {
      const result = difference(featureCollection([base, clip]));
      return result ? asPolygonalFeature(result.geometry) : null;
   } catch {
      return base;
   }
}

/**
 * Splits a simulated property's farmland the same way the jordprisberegner pipeline does:
 * crop codes decide omdrift versus permanent grass, then §3 is cut out of both so the three areas do not overlap.
 * Returns null when the property has no farmland to split.
 */
export function partitionSimulatedMarkShares(input: {
   propertyGeometry: any;
   farmlandFeatures: Array<{ geometry?: any; properties?: Record<string, unknown> | null }>;
   section3Features: Array<{ geometry?: any }>;
}): SimulatedMarkShareGeometries | null {
   const propertyFeature = asPolygonalFeature(input.propertyGeometry);
   if (!propertyFeature) return null;

   const rotationParts: PolygonalFeature[] = [];
   const nonRotationParts: PolygonalFeature[] = [];
   for (const farmlandFeature of input.farmlandFeatures) {
      const fieldFeature = asPolygonalFeature(farmlandFeature.geometry);
      if (!fieldFeature) continue;
      const overlap = intersectFeatures(propertyFeature, fieldFeature);
      if (!overlap) continue;
      if (isFieldOutsideRotation(farmlandFeature)) {
         nonRotationParts.push(overlap);
      } else {
         rotationParts.push(overlap);
      }
   }

   const overlapFeature = unionFeatures([...rotationParts, ...nonRotationParts]);
   if (!overlapFeature) return null;

   const nonRotationFeature = unionFeatures(nonRotationParts);
   const rotationFeature = nonRotationFeature
      ? differenceFeatures(overlapFeature, nonRotationFeature) ?? unionFeatures(rotationParts)
      : overlapFeature;

   const section3Parts: PolygonalFeature[] = [];
   for (const section3Feature of input.section3Features) {
      const natureFeature = asPolygonalFeature(section3Feature.geometry);
      if (!natureFeature) continue;
      const overlap = intersectFeatures(overlapFeature, natureFeature);
      if (overlap) section3Parts.push(overlap);
   }
   const section3Feature = unionFeatures(section3Parts);

   const omdriftFeature = rotationFeature
      ? differenceFeatures(rotationFeature, section3Feature)
      : null;
   const ikkeOmdriftFeature = nonRotationFeature
      ? differenceFeatures(nonRotationFeature, section3Feature)
      : null;

   return {
      omdriftGeometry: omdriftFeature?.geometry ?? null,
      ikkeOmdriftGeometry: ikkeOmdriftFeature?.geometry ?? null,
      section3Geometry: section3Feature?.geometry ?? null,
   };
}
